import { focusRing, PageHeaderCalloutDiv, PageHeaderIconButton, Tooltip } from "@platform/style";
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListCheck } from "@fortawesome/sharp-solid-svg-icons";
import { faXmark } from "@fortawesome/sharp-regular-svg-icons";
import { collectGAEvent, hasPermission, isFeatureEnabled } from "@platform/core";
import { eventDispatchers } from "../../utils/storage";
import { HeaderButtonFunction } from "@platform/chatwindow-utils";

interface MyWorkItemsButtonProps {
  disabled?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

const myWorkItemsButtonPageHeaderFunction: HeaderButtonFunction = () => {
  return <MyWorkItemsButton />;
};

export const MyWorkItemsButton = ({ disabled, onOpen, onClose }: MyWorkItemsButtonProps) => {
  const [workItemOpened, setWorkItemOpened] = React.useState(false);
  const [showWorkItemCloseButton, setShowWorkItemCloseButton] = React.useState(false);
  const [showWorkItemButton, setShowWorkItemButton] = React.useState<boolean>(false);
  const [userHasGeneralWorkItemsAccess, setUserHasGeneralWorkItemsAccess] =
    React.useState<boolean>(false);
  const [isMyWorkItemsEnabled, setIsMyWorkItemsEnabled] = React.useState<boolean>(false);
  React.useEffect(() => {
    (async () => {
      setUserHasGeneralWorkItemsAccess(await hasPermission("WorkItemManagement.generalAccess"));
      setIsMyWorkItemsEnabled(await isFeatureEnabled("Feature.ManageMyWorkItems"));
    })();
  }, []);

  React.useEffect(() => {
    window.addEventListener("closeMyWorkItems", async (_e: Event) => {
      setShowWorkItemCloseButton(false);
      setWorkItemOpened(false);
      setShowWorkItemButton(true);
    });
    eventDispatchers.forEach((event) => {
      if (event !== "openMyWorkItems") {
        window.addEventListener(event, async (_e: Event) => {
          setShowWorkItemButton(false);
        });
      }
    });

    return () => {
      window.removeEventListener("closeMyWorkItems", (_e: Event) => {
        setShowWorkItemCloseButton(false);
        setWorkItemOpened(false);
        setShowWorkItemButton(true);
      });
      eventDispatchers.forEach(async (event) => {
        if (event !== "openMyWorkItems") {
          window.removeEventListener(event, async (_e: Event) => {
            setShowWorkItemButton(false);
          });
        }
      });
    };
  }, []);

  function openMyWorkItems() {
    collectGAEvent("button", { key: "openMyWorkItems" });

    if (!workItemOpened) {
      setWorkItemOpened(true);
      setShowWorkItemCloseButton(true);
      if (onOpen) onOpen();
      window.dispatchEvent(new Event("openMyWorkItems"));
      window.dispatchEvent(new Event("closeWorkItems"));
      window.dispatchEvent(new Event("closePaymentChangeSchedule"));
      window.dispatchEvent(new Event("closeTransactionHistory"));
      window.dispatchEvent(new Event("closeWorkGroups"));
      window.dispatchEvent(new Event("closeWorkBuilder"));
    }
    setShowWorkItemButton(false);
  }
  function closeMyWorkItems() {
    setWorkItemOpened(false);
    setShowWorkItemCloseButton(false);
    if (onClose) onClose();
    window.dispatchEvent(new Event("closeMyWorkItems"));
  }

  return userHasGeneralWorkItemsAccess ? (
    <>
      {isMyWorkItemsEnabled &&
        (!showWorkItemCloseButton ? (
          <Tooltip title="My Work Items">
            <PageHeaderIconButton
              disabled={showWorkItemButton ? !showWorkItemButton : disabled}
              onClick={openMyWorkItems}
              data-testid="toggle-myworkitems-bar-button"
              sx={focusRing}
            >
              <FontAwesomeIcon icon={faListCheck} data-testid={"MyWorkItemIcon"} />
            </PageHeaderIconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Close My Work Items">
            <PageHeaderIconButton
              onClick={closeMyWorkItems}
              data-testid="close-myworkitems-bar-button"
              sx={focusRing}
            >
              <FontAwesomeIcon icon={faXmark} data-testid={"CloseIcon"} />
              <PageHeaderCalloutDiv variant={"bottom"} />
            </PageHeaderIconButton>
          </Tooltip>
        ))}
    </>
  ) : null;
};

export default myWorkItemsButtonPageHeaderFunction;
